import { useEffect, useState } from "react";
import { Card, Table, Badge, Spinner } from "react-bootstrap";
import api from "../../../axiosConfig";
import { popup } from "../../common/popup";
import "./css/Historytoolstab.css";
import * as XLSX from "xlsx";

export default function HistoryToolsTab() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [search, setSearch] = useState("");

  /* ================= FETCH HISTORY ================= */
  const fetchHistory = async () => {
    setLoading(true);
    try {
      const res = await api.get("/maintenance/tools/history");

      const sortedData = (res.data || []).sort(
        (a, b) => new Date(b.start_time) - new Date(a.start_time),
      );

      setHistory(sortedData);
      setFilteredHistory(sortedData);
    } catch (err) {
      console.error(err);
      popup.alert("Gagal mengambil history tools");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let data = [...history];

    // FILTER TANGGAL FROM
    if (dateFrom) {
      data = data.filter((h) => new Date(h.start_time) >= new Date(dateFrom));
    }

    // FILTER TANGGAL TO
    if (dateTo) {
      const endDate = new Date(dateTo);
      endDate.setHours(23, 59, 59);
      data = data.filter((h) => new Date(h.start_time) <= endDate);
    }

    // 🔎 FILTER SEARCH OTOMATIS
    if (search) {
      const keyword = search.toLowerCase();
      data = data.filter(
        (h) =>
          h.spec?.toLowerCase().includes(keyword) ||
          h.part_name?.toLowerCase().includes(keyword) ||
          h.dikerjakan_oleh?.toLowerCase().includes(keyword) ||
          h.dicek_oleh?.toLowerCase().includes(keyword) ||
          h.issue?.toLowerCase().includes(keyword) ||
          h.solution?.toLowerCase().includes(keyword),
      );
    }

    setFilteredHistory(data);
  }, [history, dateFrom, dateTo, search]);

  /* ================= DOWNLOAD EXCEL ================= */
  const handleDownloadExcel = () => {
    if (filteredHistory.length === 0) {
      popup.alert("Data kosong, tidak bisa download");
      return;
    }

    const excelData = filteredHistory.map((h, i) => ({
      No: i + 1,
      Tanggal: formatDate(h.start_time),
      Kode: cleanText(h.spec),
      "Part Name": cleanText(h.part_name),
      Status: h.judgment,
      PIC: cleanText(h.dikerjakan_oleh),
      Dicek: cleanText(h.dicek_oleh),
      Mulai: formatTime(h.start_time),
      Selesai: formatTime(h.end_time),
      Masalah: cleanText(h.issue),
      Keterangan: cleanText(h.solution),
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "History Tools");

    const today = new Date().toISOString().split("T")[0];
    XLSX.writeFile(workbook, `history_tools_${today}.xlsx`);
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  const handleDelete = async (id) => {
    const ok = await popup.confirm("Yakin ingin menghapus data ini?");
    if (!ok) return;

    try {
      await api.delete(`/maintenance/tools/history/${id}`);

      // refresh data setelah hapus
      fetchHistory();
      popup.success("Data berhasil dihapus");
    } catch (err) {
      console.error(err);
      popup.alert("Gagal menghapus data");
    }
  };

  /* ================= HELPER ================= */
  const badge = (val) => {
    if (val === "OK") return <Badge bg="success">OK</Badge>;
    if (val === "NG") return <Badge bg="danger">NG</Badge>;
    if (val === "BUTUH_DIPERBAIKI")
      return <Badge bg="danger">BUTUH DIPERBAIKI</Badge>;
    if (val === "SEDANG_DIPERBAIKI")
      return (
        <Badge bg="warning" text="dark">
          SEDANG DIPERBAIKI
        </Badge>
      );
    if (val === "SELESAI") return <Badge bg="secondary">SELESAI</Badge>;
    return <Badge bg="secondary">-</Badge>;
  };

  // ✅ bersihkan newline dari database (TAMPILAN TIDAK BERUBAH)
  const cleanText = (val) => (val ? val.replace(/\r?\n|\r/g, "").trim() : "-");

  // TANGGAL saja
  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("id-ID", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : "-";

  // JAM saja
  const formatTime = (d) =>
    d
      ? new Date(d).toLocaleTimeString("id-ID", {
          hour: "2-digit",
          minute: "2-digit",
        })
      : "-";

  /* ================= RENDER ================= */
  return (
    <div
      className=" history-tools"
      style={{
        height: "calc(120vh - 240px)",
        marginTop: "-17px",
        padding: "18px",
      }}
    >
      <Card className="history-tools-card">
        <Card.Header className="history-tools-header">
          <div className="header-bar">
            <h2>HISTORY TOOLS</h2>
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-success"
                onClick={handleDownloadExcel}
                style={{ width: "100px" }}
              >
                Export Excel
              </button>
            </div>
          </div>
          <div className="d-flex justify-content-end align-items-center">
            {/* FILTER + SEARCH + DOWNLOAD (SEJAJAR) */}
            <div className="d-flex align-items-center gap-2">
              <input
                type="date"
                className="form-control form-control-sm"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />

              <input
                type="date"
                className="form-control form-control-sm"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />

              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="🔍 Cari..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ width: "200px" }}
              />
            </div>
          </div>
        </Card.Header>

        <div className="table-wrapper">
          <table bordered hover className="history-tools-table mb-0 ">
            <thead>
              <tr>
                <th
                  className="col-no text-center"
                  style={{ background: "#1e3a8a", color: "#fff" }}
                >
                  No
                </th>
                <th
                  className="col-date text-center"
                  style={{ background: "#1e3a8a", color: "#fff" }}
                >
                  Tanggal
                </th>
                <th
                  className="col-kode text-center"
                  style={{ background: "#1e3a8a", color: "#fff" }}
                >
                  Kode / Spec
                </th>
                <th
                  className="col-part text-center"
                  style={{ background: "#1e3a8a", color: "#fff" }}
                >
                  Part Name
                </th>
                <th
                  className="text-center"
                  style={{ background: "#1e3a8a", color: "#fff" }}
                >
                  Status
                </th>
                <th
                  className="text-center"
                  style={{ background: "#1e3a8a", color: "#fff" }}
                >
                  PIC
                </th>
                <th
                  className="text-center"
                  style={{ background: "#1e3a8a", color: "#fff" }}
                >
                  Dicek
                </th>
                <th
                  className="text-center"
                  style={{ background: "#1e3a8a", color: "#fff" }}
                >
                  Mulai
                </th>
                <th
                  className="text-center"
                  style={{ background: "#1e3a8a", color: "#fff" }}
                >
                  Selesai
                </th>
                <th
                  className="col-masalah text-center"
                  style={{ background: "#1e3a8a", color: "#fff" }}
                >
                  Masalah
                </th>
                <th
                  className="col-penyelesaian text-center"
                  style={{ background: "#1e3a8a", color: "#fff" }}
                >
                  Penyelesaian
                </th>
                <th
                  className="col-action text-center"
                  style={{ background: "#1e3a8a", color: "#fff" }}
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={11} className="text-center py-3">
                    <Spinner size="sm" /> Loading...
                  </td>
                </tr>
              ) : filteredHistory.length === 0 ? (
                <tr>
                  <td colSpan={12} className="text-center py-3">
                    History belum ada
                  </td>
                </tr>
              ) : (
                filteredHistory.map((h, i) => (
                  <tr
                    key={h.id}
                    className={h.judgment === "NG" ? "row-ng" : ""}
                  >
                    <td>{i + 1}</td>
                    <td className="text-center">{formatDate(h.start_time)}</td>
                    {/* ❗ TAMPILAN SAMA, DATA SUDAH BERSIH */}
                    <td className="text-center">{cleanText(h.spec)}</td>
                    <td className="p-2 cell-left">{cleanText(h.part_name)}</td>
                    <td className="text-center">
                      {h.status === "SEDANG_DIPERBAIKI"
                        ? badge("SEDANG_DIPERBAIKI")
                        : h.status === "BUTUH_DIPERBAIKI"
                          ? badge("BUTUH_DIPERBAIKI")
                          : badge(h.judgment)}
                    </td>

                    <td className="text-center">
                      {cleanText(h.dikerjakan_oleh)}
                    </td>
                    <td className="text-center">{cleanText(h.dicek_oleh)}</td>
                    <td className="text-center">{formatTime(h.start_time)}</td>
                    <td className="text-center">{formatTime(h.end_time)}</td>
                    <td className="text-start p-1">{cleanText(h.issue)}</td>
                    <td className="text-start p-1">{cleanText(h.solution)}</td>
                    <td className="text-center">
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(h.id)}
                      >
                        Hapus
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
